/**
 * On-device photo compression for site photos.
 *
 * Phone cameras produce 4-12 MB files; on a weak site signal that is the
 * difference between a queue that clears and one that doesn't. We resize
 * to a configurable longest edge and re-encode as JPEG before the photo
 * ever touches the network (or the offline queue).
 *
 * The limits live in Settings so they can be raised if 1600px turns out
 * too small for printed reports.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const COMPRESSION_DEFAULTS = { enabled: true, maxPx: 1600, quality: 75 };

// Formats we must not touch: animation and vectors don't survive a
// canvas round-trip, and HEIC may not decode in every browser.
const SKIP_TYPES = ['image/gif', 'image/svg+xml', 'image/bmp'];

let cached = null;
let inFlight = null;

/** Session-cached photo settings; falls back to the defaults offline. */
export async function getPhotoCompressionSettings() {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      cached = {
        enabled: data?.photo_compress_enabled !== false,
        maxPx: Number(data?.photo_compress_max_px) || COMPRESSION_DEFAULTS.maxPx,
        quality: Number(data?.photo_compress_quality) || COMPRESSION_DEFAULTS.quality,
      };
    } catch {
      cached = { ...COMPRESSION_DEFAULTS };
    }
    inFlight = null;
    return cached;
  })();
  return inFlight;
}

/** Called after an admin saves new limits. */
export function clearPhotoCompressionCache() {
  cached = null;
}

const jpegName = (name) => String(name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';

async function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      // `from-image` applies the EXIF rotation so portrait shots don't
      // come out sideways.
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch { /* fall through to <img> */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/**
 * Resize + re-encode one image. Returns a File. Anything that isn't a
 * compressible image, or that would grow, comes back untouched so we
 * never make a photo worse.
 */
export async function compressImage(file, settings = COMPRESSION_DEFAULTS) {
  if (!file || !settings?.enabled) return file;
  if (!String(file.type || '').startsWith('image/')) return file;
  if (SKIP_TYPES.includes(file.type)) return file;

  try {
    const bitmap = await loadBitmap(file);
    const w = bitmap.width;
    const h = bitmap.height;
    if (!w || !h) return file;

    const maxPx = Math.max(320, Number(settings.maxPx) || COMPRESSION_DEFAULTS.maxPx);
    const scale = Math.min(1, maxPx / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tw, th);
    ctx.drawImage(bitmap, 0, 0, tw, th);
    if (typeof bitmap.close === 'function') bitmap.close();

    const quality = Math.min(100, Math.max(30, Number(settings.quality) || COMPRESSION_DEFAULTS.quality)) / 100;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;
    // An already-small photo can come out bigger — keep the original.
    if (blob.size >= file.size && scale === 1) return file;

    return new File([blob], jpegName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });
  } catch {
    return file;
  }
}

/** Compress a whole picker selection, in order. */
export async function compressImages(files) {
  const settings = await getPhotoCompressionSettings();
  const out = [];
  for (const f of files) out.push(await compressImage(f, settings));
  return out;
}

export default compressImage;
